import { useEffect, useState } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";

export interface CompanyFeatureFlags {
  anomalyDetectionEnabled: boolean;
  multimodalRagEnabled?: boolean;
}

const DEFAULT_FEATURE_FLAGS: CompanyFeatureFlags = {
  anomalyDetectionEnabled: false,
  multimodalRagEnabled: false,
};

const flagCache = new Map<string, CompanyFeatureFlags>();
const pendingRequests = new Map<string, Promise<CompanyFeatureFlags>>();

export function clearFeatureFlagsCache(): void {
  flagCache.clear();
  pendingRequests.clear();
}

export function setFeatureFlagsCacheForTesting(
  companyId: string,
  flags: Partial<CompanyFeatureFlags>
): void {
  flagCache.set(companyId, {
    ...DEFAULT_FEATURE_FLAGS,
    ...flags,
  });
}

export async function fetchCompanyFeatureFlags(
  companyId?: string
): Promise<CompanyFeatureFlags> {
  if (!companyId) {
    return DEFAULT_FEATURE_FLAGS;
  }

  const cached = flagCache.get(companyId);
  if (cached) {
    return cached;
  }

  const inflight = pendingRequests.get(companyId);
  if (inflight) {
    return inflight;
  }

  const request = (async (): Promise<CompanyFeatureFlags> => {
    try {
      if (typeof getDoc !== "function" || typeof doc !== "function") {
        return DEFAULT_FEATURE_FLAGS;
      }
      if (
        process.env.NODE_ENV === "test" &&
        !(getDoc as any)?._isMockFunction
      ) {
        return flagCache.get(companyId) || DEFAULT_FEATURE_FLAGS;
      }
      const snapshot = await getDoc(doc(db, "featureFlags", companyId));
      if (snapshot && typeof snapshot.exists === "function" && snapshot.exists()) {
        const data = snapshot.data() || {};
        const resolved: CompanyFeatureFlags = {
          anomalyDetectionEnabled: data.anomalyDetectionEnabled === true,
          multimodalRagEnabled: data.multimodalRagEnabled === true,
        };
        flagCache.set(companyId, resolved);
        return resolved;
      }
      flagCache.set(companyId, DEFAULT_FEATURE_FLAGS);
      return DEFAULT_FEATURE_FLAGS;
    } catch (err) {
      console.error("Failed to fetch feature flags for company", companyId, err);
      return DEFAULT_FEATURE_FLAGS;
    } finally {
      pendingRequests.delete(companyId);
    }
  })();

  pendingRequests.set(companyId, request);
  return request;
}

export function useFeatureFlags(companyId?: string): {
  anomalyDetectionEnabled: boolean;
  multimodalRagEnabled: boolean;
  loading: boolean;
} {
  const [flags, setFlags] = useState<CompanyFeatureFlags>(() => {
    if (companyId && flagCache.has(companyId)) {
      return flagCache.get(companyId)!;
    }
    return DEFAULT_FEATURE_FLAGS;
  });
  const [loading, setLoading] = useState<boolean>(() => {
    return Boolean(companyId && !flagCache.has(companyId));
  });

  useEffect(() => {
    let isMounted = true;
    if (!companyId) {
      setFlags(DEFAULT_FEATURE_FLAGS);
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }

    if (flagCache.has(companyId)) {
      setFlags(flagCache.get(companyId)!);
      setLoading(false);
    }

    const isTestEnv = process.env.NODE_ENV === "test";
    const canSubscribeRealtime =
      typeof onSnapshot === "function" &&
      typeof doc === "function" &&
      (!isTestEnv || Boolean((onSnapshot as any)?._isMockFunction));

    let unsubscribe: (() => void) | undefined;
    try {
      if (canSubscribeRealtime) {
        unsubscribe = onSnapshot(
          doc(db, "featureFlags", companyId),
          (snapshot) => {
            if (!isMounted) return;
            const data =
              snapshot && typeof snapshot.exists === "function" && snapshot.exists()
                ? snapshot.data()
                : {};
            const resolved: CompanyFeatureFlags = {
              anomalyDetectionEnabled: data?.anomalyDetectionEnabled === true,
              multimodalRagEnabled: data?.multimodalRagEnabled === true,
            };
            flagCache.set(companyId, resolved);
            setFlags(resolved);
            setLoading(false);
          },
          (err) => {
            console.error("Failed to subscribe to feature flags for company", companyId, err);
            if (isMounted) setLoading(false);
          }
        );
      } else {
        setLoading(!flagCache.has(companyId));
        fetchCompanyFeatureFlags(companyId).then((resolved) => {
          if (isMounted) {
            setFlags(resolved);
            setLoading(false);
          }
        });
      }
    } catch (e) {
      setLoading(!flagCache.has(companyId));
      fetchCompanyFeatureFlags(companyId).then((resolved) => {
        if (isMounted) {
          setFlags(resolved);
          setLoading(false);
        }
      });
    }

    return () => {
      isMounted = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [companyId]);

  return {
    anomalyDetectionEnabled: flags.anomalyDetectionEnabled,
    multimodalRagEnabled: Boolean(flags.multimodalRagEnabled),
    loading,
  };
}
